/**
 * Controls for the JSON columns, so authoring one does not mean typing braces.
 *
 * Every JSON column in the lab schema is described in `support/labSpecSchema`, and this file turns
 * a description into a form. A key that is a closed list becomes a select, a boolean becomes a tick
 * box, a nullable boolean becomes three buttons, a list of device keys becomes chips with a picker,
 * and a bag of caller-chosen values becomes rows of key and value. What is left - a value that is
 * genuinely arbitrary - is the only thing anybody still types.
 *
 * Three rules hold everywhere in here, and they are the reason it is worth having at all.
 *
 * Nothing is lost. A spec may hold a key no description covers, either because it is older than the
 * description or newer than this file, and a form that dropped it on save would corrupt a working
 * item. Unrecognised keys are counted, said out loud, and carried through untouched; the JSON view
 * opens by itself when there are any, because the only honest way to show a key nothing can render
 * is as text.
 *
 * An unset key is absent, not empty. Clearing a number deletes it rather than writing null, because
 * `{"timeout_seconds": null}` is not the same as saying nothing - the compile reads the first as an
 * answer and falls back on the second. This is also why the empty object is emitted as null: an
 * item with `action` set to `{}` compiles to nothing, and it should read as nothing.
 *
 * The default is always stated - once. The failures these columns produce are the quiet ones: a step
 * with no expectation passes whatever happened, and one with no blast radius is silently excluded from
 * every fleet sweep. So what happens when a key is left out is documented for every key, in the help
 * menu at the top of the form. It is not repeated under each control as well: a form of twenty fields
 * each carrying two sentences is one nobody reads, which fixes nothing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import { HbrIcon } from '@harbor/elements-react-standalone';
import { caretDown, caretRight, plus, trash, x } from '@harbor/elements/icons';
import StbButton from '../security-toolbox-ui/StbButton';
import { LabOption } from '../../support/labService';
import {
  LabSpecField,
  LabSpecSuggestionSource,
  LAB_JSON_SCHEMAS,
  LAB_SCOPE_FIELDS,
  LabJsonSchema,
  labSchemaExtraKeys,
} from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabHelpMenu,
  LabJsonField,
  LabOptionSelect,
  LabTristate,
} from './LabShared';

/* ------------------------------------------------------------------------- *
 * A bag of caller-chosen keys
 * ------------------------------------------------------------------------- */

type ValueKind = 'text' | 'number' | 'boolean' | 'json';

const VALUE_KINDS: LabOption<ValueKind>[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'json', label: 'List or object' },
];

/**
 * Key, type, value, delete - the same track list for the heading row and every row under it.
 *
 * Written out as one literal so Tailwind can see it, and shared so the heading cannot drift out of
 * alignment with the cells it names.
 */
const KV_COLUMNS = 'md:grid-cols-[minmax(0,1fr)_8.5rem_minmax(0,2fr)_1.75rem]';

const kindOf = (value: any): ValueKind => {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string') return 'text';

  return 'json';
};

const textOf = (value: any, kind: ValueKind): string => {
  if (value === undefined || value === null) return '';
  if (kind === 'json') return JSON.stringify(value);

  return String(value);
};

type Row = { id: number; key: string; kind: ValueKind; text: string; error: string | null };

const rowsFrom = (value: Record<string, any> | null | undefined, seed: number): Row[] =>
  Object.entries(value || {}).map(([key, entry], index) => {
    const kind = kindOf(entry);

    return { id: seed + index, key, kind, text: textOf(entry, kind), error: null };
  });

const parseRow = (row: Row): { value: any; error: string | null } => {
  if (row.kind === 'boolean') return { value: row.text === 'true', error: null };

  if (row.kind === 'number') {
    if (row.text.trim() === '') return { value: 0, error: null };
    if (!Number.isFinite(Number(row.text))) return { value: null, error: 'That is not a number.' };

    return { value: Number(row.text), error: null };
  }

  if (row.kind === 'json') {
    if (!row.text.trim()) return { value: null, error: null };

    try {
      return { value: JSON.parse(row.text), error: null };
    } catch (exc) {
      return { value: null, error: (exc as Error).message || 'That is not valid JSON.' };
    }
  }

  return { value: row.text, error: null };
};

/**
 * A flat object, as rows.
 *
 * For the columns whose keys are the author's own to invent - options, metadata, a request payload -
 * where there is no list to offer and so no select to build. The per-row type is a select rather
 * than a guess from the text, because `{"port": "443"}` and `{"port": 443}` are different documents
 * and a string where a number was meant is the kind of thing an API refuses without saying why.
 *
 * Rows are held here rather than derived from the value on every render, so that renaming a key does
 * not reorder the list under the cursor and clearing a value does not delete the row.
 *
 * Laid out as a table, and that is not cosmetic: with a label on every control, three rows of
 * key/type/value read as "Key customer Type Text Value PseudoCo Key owner Type..." - the words that
 * repeat are the loudest thing on the screen and the two that matter are buried between them. So the
 * names are said once, in a heading row, and each row is three cells and a delete.
 */
export const LabKeyValueEditor: React.FC<{
  label?: string;
  value: Record<string, any> | null | undefined;
  onChange: (value: Record<string, any> | null, error: string | null) => void;
  keyLabel?: string;
  valueLabel?: string;
  helpText?: string;
  /** Keys worth offering even though anything is allowed. */
  suggestions?: LabOption[];
  /** Changes when a different row is loaded into the form, so the editor re-seeds. */
  resetKey?: string | number;
  disabled?: boolean;
}> = ({
  label,
  value,
  onChange,
  keyLabel = 'Key',
  valueLabel = 'Value',
  helpText,
  suggestions,
  resetKey,
  disabled = false,
}) => {
  const nextId = useRef(1);
  const [rows, setRows] = useState<Row[]>(() => {
    const seeded = rowsFrom(value, nextId.current);
    nextId.current += seeded.length + 1;

    return seeded;
  });

  // Only when a different row is loaded. Re-seeding on every value change would fight the rows
  // state it exists to protect.
  useEffect(() => {
    const seeded = rowsFrom(value, nextId.current);
    nextId.current += seeded.length + 1;
    setRows(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emit = useCallback(
    (next: Row[]) => {
      setRows(next);

      const seen = new Set<string>();
      const built: Record<string, any> = {};
      let error: string | null = null;

      next.forEach((row) => {
        const key = row.key.trim();

        if (!key) return;

        if (seen.has(key)) {
          error = error || `"${key}" is in there twice. The second one would win, silently.`;
          return;
        }

        const parsed = parseRow(row);

        if (parsed.error) {
          error = error || `${key}: ${parsed.error}`;
          return;
        }

        seen.add(key);
        built[key] = parsed.value;
      });

      onChange(Object.keys(built).length ? built : null, error);
    },
    [onChange],
  );

  const patch = (id: number, changes: Partial<Row>) =>
    emit(rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));

  const add = (key = '') => {
    const id = nextId.current;
    nextId.current += 1;

    emit([...rows, { id, key, kind: 'text', text: '', error: null }]);
  };

  const unused = useMemo(
    () => (suggestions || []).filter((option) => !rows.some((row) => row.key === option.value)),
    [suggestions, rows],
  );

  return (
    <div className="flex flex-col gap-2">
      {label && <span className="text-sm font-medium text-gray-700">{label}</span>}

      {!rows.length && (
        <p className="m-0 text-xs text-gray-500">Nothing set. Empty is a valid answer here.</p>
      )}

      {/* One heading rather than a label per cell. Hidden on a narrow screen, where the row stacks
          and the placeholders are what name the cells. */}
      {!!rows.length && (
        <div
          className={`hidden gap-2 text-xs font-medium uppercase tracking-wide text-gray-500 md:grid ${KV_COLUMNS}`}
        >
          <span>{keyLabel}</span>
          <span>Type</span>
          <span>{valueLabel}</span>
          <span />
        </div>
      )}

      {rows.map((row) => {
        const parsed = parseRow(row);

        return (
          <div className={`grid grid-cols-1 items-center gap-2 ${KV_COLUMNS}`} key={row.id}>
            <TextField
              value={row.key}
              onChange={(evt) => patch(row.id, { key: evt.target.value })}
              size="small"
              disabled={disabled}
              placeholder={keyLabel.toLowerCase()}
              inputProps={{ 'aria-label': keyLabel }}
              fullWidth
            />

            <LabOptionSelect
              label="Type"
              hideLabel
              value={row.kind}
              options={VALUE_KINDS}
              disabled={disabled}
              onChange={(kind) =>
                patch(row.id, {
                  kind,
                  // Re-rendered rather than reinterpreted: "443" as a number is 443, but
                  // "true" as a number is nothing, and carrying the text across would leave a
                  // row that reads as filled in and parses to garbage.
                  text:
                    kind === 'boolean'
                      ? row.text === 'true'
                        ? 'true'
                        : 'false'
                      : kind === 'number' && !Number.isFinite(Number(row.text))
                        ? ''
                        : row.text,
                })
              }
            />

            {row.kind === 'boolean' ? (
              <LabOptionSelect
                label={valueLabel}
                hideLabel
                value={row.text === 'true' ? 'true' : 'false'}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                disabled={disabled}
                onChange={(next) => patch(row.id, { text: next })}
              />
            ) : (
              <TextField
                value={row.text}
                onChange={(evt) => patch(row.id, { text: evt.target.value })}
                size="small"
                disabled={disabled}
                error={!!parsed.error}
                placeholder={
                  row.kind === 'json' ? '["a", "b"] or {"a": 1}' : valueLabel.toLowerCase()
                }
                inputProps={{ 'aria-label': valueLabel }}
                fullWidth
              />
            )}

            <button
              type="button"
              className="justify-self-start border-0 bg-transparent p-1 text-gray-500 hover:text-red-700"
              aria-label={`Remove ${row.key || 'this row'}`}
              disabled={disabled}
              onClick={() => emit(rows.filter((entry) => entry.id !== row.id))}
            >
              <HbrIcon asset={trash} className="text-sm" />
            </button>

            {/* Under the row rather than under the value, so one bad row does not shunt the
                others out of line. */}
            {parsed.error && (
              <p className="m-0 text-xs text-red-700 md:col-span-4">{parsed.error}</p>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        <StbButton role="add" size="xsmall" disabled={disabled} onClick={async () => add()}>
          + Add {keyLabel.toLowerCase()}
        </StbButton>

        {/* The suggestions are the point of the "+ New" affordance here: a key somebody would have
            had to know about is one click, and the rest is still open. */}
        {unused.map((option) => (
          <button
            key={option.value}
            type="button"
            className="rounded-full border border-dashed border-gray-400 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
            title={option.hint}
            disabled={disabled}
            onClick={() => add(option.value)}
          >
            <HbrIcon asset={plus} className="mr-0.5 text-[10px]" />
            {option.label}
          </button>
        ))}
      </div>

      {helpText && <span className="text-xs text-gray-500">{helpText}</span>}
    </div>
  );
};

/* ------------------------------------------------------------------------- *
 * Lists
 * ------------------------------------------------------------------------- */

/**
 * A list of strings, as chips with a picker.
 *
 * The suggestions are the whole reason this is not a text field: `devices` on a health check is a
 * list of device keys, and a key typed by hand that no device has monitors nothing at all - the
 * compile reports it, one screen and one save later. Offering the section's own keys makes the
 * common case a click, and free text stays available because a section can gain a device after the
 * item was written.
 */
export const LabStringListField: React.FC<{
  label: string;
  value: string[] | null | undefined;
  onChange: (value: string[] | null) => void;
  helpText?: string;
  suggestions?: string[];
  emptyNote?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, helpText, suggestions, emptyNote, disabled = false }) => {
  const [typed, setTyped] = useState('');
  const chosen = useMemo(() => value || [], [value]);

  const add = (entry: string) => {
    const clean = entry.trim();

    if (!clean || chosen.includes(clean)) return;

    onChange([...chosen, clean]);
    setTyped('');
  };

  const available = (suggestions || []).filter((entry) => !chosen.includes(entry));

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-gray-700">{label}</span>

      {!chosen.length ? (
        <p className="m-0 text-xs text-gray-500">{emptyNote || 'Nothing chosen.'}</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {chosen.map((entry) => (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs"
              key={entry}
            >
              <span className="font-mono">{entry}</span>
              {!disabled && (
                <button
                  type="button"
                  className="border-0 bg-transparent p-0 leading-none text-gray-500 hover:text-red-700"
                  aria-label={`Remove ${entry}`}
                  onClick={() => onChange(chosen.filter((item) => item !== entry) || null)}
                >
                  <HbrIcon asset={x} className="text-sm" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!!available.length && (
        <div className="flex flex-wrap gap-1">
          {available.map((entry) => (
            <button
              key={entry}
              type="button"
              className="rounded-full border border-dashed border-gray-400 bg-white px-2 py-0.5 font-mono text-xs text-gray-700 hover:bg-gray-50"
              disabled={disabled}
              onClick={() => add(entry)}
            >
              <HbrIcon asset={plus} className="mr-0.5 text-[10px]" />
              {entry}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2">
        <TextField
          label="Add another"
          value={typed}
          onChange={(evt) => setTyped(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.key === 'Enter') {
              evt.preventDefault();
              add(typed);
            }
          }}
          size="small"
          disabled={disabled}
          className="min-w-[10rem] flex-1"
        />
        <StbButton
          role="add"
          size="xsmall"
          disabled={disabled || !typed.trim()}
          onClick={async () => add(typed)}
        >
          Add
        </StbButton>
      </div>

      {helpText && <span className="text-xs text-gray-500">{helpText}</span>}
    </div>
  );
};

/** Several choices from a closed list. Tick boxes rather than a multi-select: there are never many. */
export const LabOptionListField: React.FC<{
  label: string;
  value: string[] | null | undefined;
  options: LabOption[];
  onChange: (value: string[] | null) => void;
  helpText?: string;
  disabled?: boolean;
}> = ({ label, value, options, onChange, helpText, disabled = false }) => {
  const chosen = value || [];

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-gray-700">{label}</span>

      <div className="flex flex-col gap-1">
        {options.map((option) => (
          <LabCheckbox
            key={option.value}
            label={option.label}
            checked={chosen.includes(option.value)}
            disabled={disabled}
            helpText={option.hint}
            onChange={(ticked) => {
              const next = ticked
                ? [...chosen, option.value]
                : chosen.filter((entry) => entry !== option.value);

              onChange(next.length ? next : null);
            }}
          />
        ))}
      </div>

      {helpText && <span className="text-xs text-gray-500">{helpText}</span>}
    </div>
  );
};

/* ------------------------------------------------------------------------- *
 * Preconditions
 * ------------------------------------------------------------------------- */

/**
 * The `requires` object, as a set of scopes.
 *
 * The loader refuses any key that is not one of the nine scopes, so this is the one JSON column
 * where free text is simply wrong - a scope nobody evaluates is carried, ignored, and reported one
 * compile later. Integrity is the only one whose value is a choice rather than "this is required",
 * and it is the one worth getting right: a step that expects an already-compromised host is the
 * whole premise of a threat-protection exercise.
 */
export const LabScopeEditor: React.FC<{
  value: Record<string, any> | null | undefined;
  onChange: (value: Record<string, any> | null) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  const current = value || {};
  const chosen = Object.keys(current);

  const set = (scope: string, entry: any) => {
    const next = { ...current, [scope]: entry };

    onChange(Object.keys(next).length ? next : null);
  };

  const drop = (scope: string) => {
    const next = { ...current };
    delete next[scope];

    onChange(Object.keys(next).length ? next : null);
  };

  const available = LAB_SCOPE_FIELDS.filter((scope) => !chosen.includes(scope.value));

  return (
    <div className="flex flex-col gap-2">
      {!chosen.length && (
        <p className="m-0 text-xs text-gray-500">
          Nothing is checked before this runs. That is fine for most steps - add a scope where a
          failure would otherwise be unexplainable.
        </p>
      )}

      {LAB_SCOPE_FIELDS.filter((scope) => chosen.includes(scope.value)).map((scope) => (
        <div
          className="flex flex-wrap items-center gap-2 rounded border border-gray-200 px-2 py-1.5"
          key={scope.value}
        >
          <span className="text-sm font-medium text-gray-700">{scope.label}</span>

          {scope.states ? (
            <div className="min-w-[14rem]">
              <LabOptionSelect
                label="Expected state"
                value={String(current[scope.value] ?? 'clean')}
                options={scope.states}
                disabled={disabled}
                onChange={(state) => set(scope.value, state)}
              />
            </div>
          ) : (
            <span className="text-xs text-gray-500">
              {scope.hint || 'Required before the work is attempted.'}
            </span>
          )}

          <button
            type="button"
            className="ml-auto border-0 bg-transparent p-1 text-gray-500 hover:text-red-700"
            aria-label={`Remove the ${scope.label.toLowerCase()} precondition`}
            disabled={disabled}
            onClick={() => drop(scope.value)}
          >
            <HbrIcon asset={trash} className="text-sm" />
          </button>
        </div>
      ))}

      {!!available.length && (
        <div className="flex flex-wrap gap-1">
          {available.map((scope) => (
            <button
              key={scope.value}
              type="button"
              className="rounded-full border border-dashed border-gray-400 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
              title={scope.hint}
              disabled={disabled}
              onClick={() => set(scope.value, scope.states ? 'clean' : true)}
            >
              <HbrIcon asset={plus} className="mr-0.5 text-[10px]" />
              {scope.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------- *
 * One arbitrary value
 * ------------------------------------------------------------------------- */

/**
 * A single JSON value, which may be a scalar.
 *
 * `LabJsonField` deliberately refuses anything that is not an object, because every column it edits
 * is one. This is for the value *inside* a spec - the right-hand side of a fact assertion, which is
 * legitimately the string "up", the number 3, or a list. Bare text is read as a string, so nobody
 * has to remember to quote it.
 */
export const LabJsonValueField: React.FC<{
  label: string;
  value: any;
  onChange: (value: any, error: string | null) => void;
  helpText?: string;
  resetKey?: string | number;
  disabled?: boolean;
}> = ({ label, value, onChange, helpText, resetKey, disabled = false }) => {
  const [text, setText] = useState(() =>
    value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const handle = (next: string) => {
    setText(next);

    if (!next.trim()) {
      setError(null);
      onChange(undefined, null);
      return;
    }

    // Only the shapes JSON can express are parsed; anything else is the string it looks like.
    // `up` is a state, not a syntax error, and making somebody type `"up"` here would be a tax
    // on the common case for the benefit of the rare one.
    if (/^[[{"]|^-?\d|^(true|false|null)$/.test(next.trim())) {
      try {
        setError(null);
        onChange(JSON.parse(next), null);
        return;
      } catch (exc) {
        const message = (exc as Error).message || 'That is not valid JSON.';
        setError(message);
        onChange(undefined, message);
        return;
      }
    }

    setError(null);
    onChange(next, null);
  };

  return (
    <TextField
      label={label}
      value={text}
      onChange={(evt) => handle(evt.target.value)}
      size="small"
      disabled={disabled}
      error={!!error}
      helperText={error || helpText}
      placeholder='up, 3, true, ["a","b"]'
      fullWidth
    />
  );
};

/* ------------------------------------------------------------------------- *
 * The builder
 * ------------------------------------------------------------------------- */

/** Rows the form already has in hand, so a list of keys can be offered rather than typed. */
export type LabSpecContext = {
  deviceKeys?: string[];
  criterionKeys?: string[];
  optionKeys?: string[];
  stepKeys?: string[];
};

const suggestionsFor = (
  source: LabSpecSuggestionSource | undefined,
  context: LabSpecContext,
): string[] | undefined => {
  if (!source) return undefined;

  return context[source];
};

/**
 * A JSON column, as a form.
 *
 * The escape hatch is not a fallback, it is a promise: an author may always see and edit the
 * document itself, because these columns predate this form and will outlive it. It opens by itself
 * when the spec holds a key no field covers, since that is the only view that can show one.
 */
export const LabSpecBuilder: React.FC<{
  schema: LabJsonSchema;
  value: Record<string, any> | null | undefined;
  onChange: (value: Record<string, any> | null, error: string | null) => void;
  context?: LabSpecContext;
  /** Changes when a different row is loaded into the form. */
  resetKey?: string | number;
  /** Rendered beside the title - for the execution-method select that pairs with a spec. */
  actions?: React.ReactNode;
  /** Rendered under the fields, e.g. a warning about the pairing above. */
  children?: React.ReactNode;
  disabled?: boolean;
}> = ({ schema, value, onChange, context = {}, resetKey, actions, children, disabled = false }) => {
  const extras = useMemo(() => labSchemaExtraKeys(schema, value), [schema, value]);
  const [raw, setRaw] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      (schema.groups || []).map((group) => [group.title, !group.collapsed]),
    ),
  );

  // A spec nothing can render has to be shown as text. Tracked as an effect rather than as initial
  // state because the modals stay mounted between rows: opening an item with an unrecognised key
  // after one without would otherwise hide it.
  useEffect(() => {
    if (extras.length) setRaw(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, extras.length]);

  const spec = useMemo(() => value || {}, [value]);
  const occupied = Object.keys(spec).length > 0;

  const set = useCallback(
    (key: string, next: any) => {
      const merged = { ...spec };

      // Absent, not empty. `{"timeout_seconds": null}` reads as an answer to the compile and
      // "" reads as a name; saying nothing is what makes the documented default apply.
      if (next === undefined || next === null || next === '') {
        delete merged[key];
      } else {
        merged[key] = next;
      }

      onChange(Object.keys(merged).length ? merged : null, null);
    },
    [spec, onChange],
  );

  /**
   * The keys a freeform editor offers as buttons: the documented ones, plus whatever the surrounding
   * form knows. An attachment's overrides are the case that needs both - the schema knows nothing
   * about which options the item being attached declares, and that is the only list worth offering.
   */
  const freeformSuggestions = useMemo(() => {
    const freeform = schema.freeform;

    if (!freeform) return undefined;

    const merged: LabOption[] = [...(freeform.suggestions || [])];

    (freeform.suggestionSource ? suggestionsFor(freeform.suggestionSource, context) || [] : []).forEach(
      (key) => {
        if (!merged.some((option) => option.value === key)) merged.push({ value: key, label: key });
      },
    );

    return merged.length ? merged : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, context.optionKeys, context.deviceKeys, context.criterionKeys, context.stepKeys]);

  const field = (definition: LabSpecField) => {
    if (definition.when && !definition.when(spec)) return null;

    // The help line is what the key is, and nothing else. What happens when it is left out used to be
    // appended here as well, which doubled the length of every line on a form with twenty of them -
    // and it was already printed, per key, in the help menu at the top. Said once, in the place that
    // is opened when somebody is asking.
    const help = definition.help;
    const current = spec[definition.key];

    switch (definition.kind) {
      case 'option':
        return (
          <LabOptionSelect
            key={definition.key}
            label={definition.label}
            value={current ?? ''}
            options={definition.options || []}
            onChange={(next) => set(definition.key, next)}
            helpText={help}
            allowEmpty
            emptyLabel="Not set"
            disabled={disabled}
          />
        );

      case 'optionList':
        return (
          <LabOptionListField
            key={definition.key}
            label={definition.label}
            value={current}
            options={definition.options || []}
            onChange={(next) => set(definition.key, next)}
            helpText={help}
            disabled={disabled}
          />
        );

      case 'boolean':
        return (
          <LabCheckbox
            key={definition.key}
            label={definition.label}
            checked={!!current}
            onChange={(next) =>
              // `false` is an answer for the keys that default to true, so it is written rather
              // than deleted - `scored: false` is the only way to say "do not score this".
              set(definition.key, next ? true : false)
            }
            helpText={help}
            disabled={disabled}
          />
        );

      case 'tristate':
        return (
          <LabTristate
            key={definition.key}
            label={definition.label}
            value={current}
            onChange={(next) => (next === null ? set(definition.key, undefined) : set(definition.key, next))}
            helpText={help}
          />
        );

      case 'number':
        return (
          <TextField
            key={definition.key}
            label={definition.unit ? `${definition.label} (${definition.unit})` : definition.label}
            type="number"
            value={current ?? ''}
            onChange={(evt) =>
              set(definition.key, evt.target.value === '' ? undefined : Number(evt.target.value))
            }
            helperText={help}
            size="small"
            disabled={disabled}
            fullWidth
          />
        );

      case 'multiline':
        return (
          <TextField
            key={definition.key}
            label={definition.label}
            value={current ?? ''}
            onChange={(evt) => set(definition.key, evt.target.value)}
            helperText={help}
            size="small"
            multiline
            minRows={2}
            disabled={disabled}
            fullWidth
          />
        );

      case 'stringList':
        return (
          <LabStringListField
            key={definition.key}
            label={definition.label}
            value={current}
            onChange={(next) => set(definition.key, next)}
            helpText={help}
            suggestions={suggestionsFor(definition.suggestions, context)}
            emptyNote={definition.fallback ? `Nothing chosen. ${definition.fallback}` : undefined}
            disabled={disabled}
          />
        );

      case 'keyvalue':
        return (
          <LabKeyValueEditor
            key={definition.key}
            label={definition.label}
            value={current}
            onChange={(next, error) => {
              if (error) {
                onChange(value ?? null, error);
                return;
              }

              set(definition.key, next);
            }}
            helpText={help}
            resetKey={`${resetKey ?? 'new'}:${definition.key}`}
            disabled={disabled}
          />
        );

      case 'scopes':
        return (
          <div className="flex flex-col gap-1.5" key={definition.key || 'requires'}>
            <span className="text-sm font-medium text-gray-700">{definition.label}</span>
            <span className="text-xs text-gray-500">{help}</span>
            <LabScopeEditor
              value={definition.key ? current : spec}
              onChange={(next) =>
                definition.key ? set(definition.key, next) : onChange(next, null)
              }
              disabled={disabled}
            />
          </div>
        );

      case 'factAssertion':
      case 'healthAssertion':
        return (
          <div className="rounded border border-gray-200 p-2" key={definition.key}>
            <LabSpecBuilder
              schema={
                LAB_JSON_SCHEMAS[
                  definition.kind === 'factAssertion'
                    ? 'criterionFactAssertion'
                    : 'criterionHealthAssertion'
                ]
              }
              value={current}
              onChange={(next, error) => (error ? onChange(value ?? null, error) : set(definition.key, next))}
              context={context}
              resetKey={`${resetKey ?? 'new'}:${definition.key}`}
              disabled={disabled}
            />
          </div>
        );

      case 'json':
        return (
          <LabJsonValueField
            key={definition.key}
            label={definition.label}
            value={current}
            onChange={(next, error) => (error ? onChange(value ?? null, error) : set(definition.key, next))}
            helpText={help}
            resetKey={`${resetKey ?? 'new'}:${definition.key}`}
            disabled={disabled}
          />
        );

      case 'text':
      default:
        return (
          <TextField
            key={definition.key}
            label={definition.label}
            value={current ?? ''}
            onChange={(evt) => set(definition.key, evt.target.value)}
            helperText={help}
            size="small"
            disabled={disabled}
            fullWidth
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-gray-700">{schema.title}</span>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          <button
            type="button"
            className="border-0 bg-transparent p-0 text-xs text-blue-800 underline"
            onClick={() => setRaw(!raw)}
          >
            {raw ? 'Back to the form' : 'Edit as JSON'}
          </button>
        </div>
      </div>

      <LabHelpMenu
        schema={schema}
        occupied={occupied}
        onUseExample={disabled ? undefined : (example) => onChange(example, null)}
      />

      {/* Said, not swallowed. Somebody wrote these and something may read them; the JSON view is
          the only place they are visible, so it is opened rather than offered. */}
      {!!extras.length && (
        <p className="m-0 text-xs text-amber-800">
          {`${extras.length === 1 ? 'One key here is' : `${extras.length} keys here are`} not on the form: ${extras.join(', ')}. They are kept exactly as written - edit them below.`}
        </p>
      )}

      {raw || schema.freeform ? (
        schema.freeform ? (
          <LabKeyValueEditor
            value={value}
            onChange={onChange}
            keyLabel={schema.freeform.keyLabel}
            valueLabel={schema.freeform.valueLabel}
            helpText={schema.freeform.help}
            suggestions={freeformSuggestions}
            resetKey={resetKey}
            disabled={disabled}
          />
        ) : (
          <LabJsonField
            label={`${schema.title}, as written`}
            value={value}
            onChange={onChange}
            resetKey={`${resetKey ?? 'new'}:raw`}
            rows={8}
            helpText="Everything the form above can set, and anything it cannot. Refused here rather than sent, because a 400 from the CRUD API says invalid and not which line."
          />
        )
      ) : (
        (schema.groups || []).map((group) => {
          const rendered = group.fields.map(field).filter(Boolean);

          if (!rendered.length) return null;

          const open = openGroups[group.title] !== false;

          return (
            <section className="border-t border-gray-100 pt-2" key={group.title}>
              <button
                type="button"
                className="flex w-full items-center gap-1.5 border-0 bg-transparent p-0 text-left"
                aria-expanded={open}
                onClick={() => setOpenGroups({ ...openGroups, [group.title]: !open })}
              >
                <HbrIcon
                  asset={open ? caretDown : caretRight}
                  className="text-sm text-gray-500"
                />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {group.title}
                </span>
              </button>

              {open && (
                <div className="mt-2 flex flex-col gap-3">
                  {group.hint && <p className="m-0 text-xs text-gray-500">{group.hint}</p>}
                  {rendered}
                </div>
              )}
            </section>
          );
        })
      )}

      {children}
    </div>
  );
};
